import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';

import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

import { DashboardContent } from 'src/layouts/dashboard';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { Iconify } from 'src/components/iconify';
import { PermissionButton } from 'src/routes/components';

import { useGrandPrixConfig } from './hooks/useGrandPrixConfig';
import type {
  FieldProps,
  GrandPrixCard,
  GrandPrixCardMap,
  GrandPrixFormProps,
  GrandPrixSlot,
  MatchLite,
  SaveBarProps,
  TranslateFn,
} from './types';
import {
  GRAND_PRIZE_TABS,
  buildPatchPayload,
  getCandidateMatchesBySlot,
  getErrorMessage,
  getMatchOptionLabel,
  getMatchStatusText,
  getMatchTitle,
  isCardEqual,
} from './utils';

const I18N_PREFIX = 'module.worldCup.grandPrix';

const tGrand = (t: TranslateFn, key: string, options?: Record<string, unknown>) =>
  t(`${I18N_PREFIX}.${key}`, options);

export const MainPage = () => {
  const t = useTranslate();
  const notify = useNotify();

  const { cards, matchesByStage, loading, saving, refetch, patchCard } = useGrandPrixConfig();

  const [activeSlot, setActiveSlot] = useState<GrandPrixSlot>('sf1');
  const [draftMap, setDraftMap] = useState<GrandPrixCardMap>(cards);

  useEffect(() => {
    setDraftMap(cards);
  }, [cards]);

  const activeCard = draftMap[activeSlot];
  const originalCard = cards[activeSlot];

  const isDirty = useMemo(() => !isCardEqual(activeCard, originalCard), [activeCard, originalCard]);

  const matchOptions = useMemo(() => {
    const candidates = getCandidateMatchesBySlot(activeSlot, matchesByStage);
    const current = activeCard?.linkedMatch;

    if (!current) return candidates;

    const exists = candidates.some((item) => String(item.id) === String(current.id));

    return exists ? candidates : [current, ...candidates];
  }, [activeCard?.linkedMatch, activeSlot, matchesByStage]);

  const updateActiveCard = useCallback(
    (next: GrandPrixCard) => {
      setDraftMap((prev) => ({
        ...prev,
        [activeSlot]: next,
      }));
    },
    [activeSlot]
  );

  const handleTabChange = useCallback((slot: GrandPrixSlot) => {
    setActiveSlot(slot);
  }, []);

  const handleDiscard = useCallback(() => {
    if (!window.confirm(tGrand(t, 'confirm.discard'))) return;

    setDraftMap((prev) => ({
      ...prev,
      [activeSlot]: originalCard,
    }));
  }, [activeSlot, originalCard, t]);

  const handleSave = useCallback(async () => {
    const payload = buildPatchPayload(activeCard, originalCard);

    if (!Object.keys(payload).length) return;

    try {
      await patchCard(activeSlot, payload);
      await refetch();

      notify(tGrand(t, 'notify.saveSuccess'), { type: 'success' });
    } catch (err) {
      notify(getErrorMessage(err) || tGrand(t, 'notify.saveFailed'), { type: 'error' });
    }
  }, [activeCard, activeSlot, notify, originalCard, patchCard, refetch, t]);

  return (
    <DashboardContent>
      <CustomBreadcrumbs
        heading={tGrand(t, 'title')}
        links={[{ name: t('resource.worldCup') }, { name: t('resource.worldCupGrandPrixConfig') }]}
        action={<GrandPrixTabs activeSlot={activeSlot} onChange={handleTabChange} />}
        sx={{ mb: 3 }}
      />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {tGrand(t, 'description')}
      </Typography>

      {loading ? (
        <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 360 }}>
          <CircularProgress size={28} />
        </Stack>
      ) : (
        <Stack spacing={3}>
          <GrandPrixForm
            config={activeCard}
            original={originalCard}
            matchOptions={matchOptions}
            onChange={updateActiveCard}
          />
        </Stack>
      )}

      {isDirty ? <SaveBar loading={saving} onDiscard={handleDiscard} onSave={handleSave} /> : null}
    </DashboardContent>
  );
};

const GrandPrixTabs = memo(
  ({
    activeSlot,
    onChange,
  }: {
    activeSlot: GrandPrixSlot;
    onChange: (slot: GrandPrixSlot) => void;
  }) => {
    const t = useTranslate();

    return (
      <Tabs value={activeSlot} onChange={(_, slot: GrandPrixSlot) => onChange(slot)}>
        {GRAND_PRIZE_TABS.map((item) => (
          <Tab key={item.slot} value={item.slot} label={tGrand(t, item.labelKey)} />
        ))}
      </Tabs>
    );
  }
);

GrandPrixTabs.displayName = 'GrandPrixTabs';

const GrandPrixForm = memo(({ config, original, matchOptions, onChange }: GrandPrixFormProps) => {
  const t = useTranslate();

  const selectedMatch = useMemo(
    () =>
      matchOptions.find((item) => String(item.id) === String(config.matchId)) ?? config.linkedMatch,
    [config.linkedMatch, config.matchId, matchOptions]
  );

  const slotCopy = useMemo(() => getSlotCopy(t, config.slot), [config.slot, t]);
  const locked = config.isLocked || original.isLocked;
  const cardState = config.cardState || original.cardState;

  const handleMatchChange = useCallback(
    (value: string) => {
      const match = matchOptions.find((item) => String(item.id) === String(value)) ?? null;

      onChange({
        ...config,
        matchId: value || null,
        linkedMatch: match,
      });
    },
    [config, matchOptions, onChange]
  );

  return (
    <>
      <Card sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h6">{tGrand(t, 'section.basic')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {slotCopy.basicDesc}
            </Typography>
          </Box>

          <Chip
            label={tGrand(t, `themeTag.${config.theme}`)}
            color={
              config.theme === 'gold'
                ? 'warning'
                : config.theme === 'brand'
                  ? 'secondary'
                  : 'primary'
            }
            size="small"
          />
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
            mt: 2.5,
          }}
        >
          <Field label={tGrand(t, 'field.match')}>
            <TextField
              select
              fullWidth
              size="small"
              value={config.matchId ?? ''}
              disabled={locked}
              onChange={(event) => handleMatchChange(event.target.value)}
            >
              <MenuItem value="">{tGrand(t, 'placeholder.select')}</MenuItem>
              {matchOptions.map((match: MatchLite) => (
                <MenuItem key={match.id} value={match.id}>
                  {getMatchOptionLabel(match, t)}
                </MenuItem>
              ))}
            </TextField>

            {selectedMatch ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: 'block' }}
              >
                {getMatchTitle(selectedMatch)} · {getMatchStatusText(selectedMatch)}
              </Typography>
            ) : null}

            {locked ? (
              <Box sx={{ mt: 1 }}>
                <Chip
                  icon={<Iconify icon="eva:lock-fill" />}
                  label={config.lockedReason || tGrand(t, 'locked.fields')}
                  color="warning"
                  size="small"
                  variant="outlined"
                />
              </Box>
            ) : null}
          </Field>

          <Field label={tGrand(t, 'field.theme')}>
            <TextField
              select
              fullWidth
              size="small"
              value={config.theme}
              onChange={(event) =>
                onChange({
                  ...config,
                  theme: event.target.value as GrandPrixCard['theme'],
                })
              }
            >
              <MenuItem value="brand">{tGrand(t, 'theme.brand')}</MenuItem>
              <MenuItem value="gold">{tGrand(t, 'theme.gold')}</MenuItem>
              <MenuItem value="secondary">{tGrand(t, 'theme.secondary')}</MenuItem>
            </TextField>
          </Field>
        </Box>
      </Card>

      <Card sx={{ p: 3 }}>
        <Typography variant="h6">{tGrand(t, 'section.copy')}</Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
            mt: 2.5,
          }}
        >
          <Field label={tGrand(t, 'field.titleZh')}>
            <TextInput
              value={config.titleZh}
              onChange={(value) => onChange({ ...config, titleZh: value })}
            />
          </Field>

          <Field label={tGrand(t, 'field.titleEn')}>
            <TextInput
              value={config.titleEn}
              onChange={(value) => onChange({ ...config, titleEn: value })}
            />
          </Field>
        </Box>
      </Card>

      <Card sx={{ p: 3 }}>
        <Typography variant="h6">{tGrand(t, 'section.vouchers')}</Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr 1fr' },
            gap: 2,
            mt: 2.5,
          }}
        >
          {[
            { key: 'totalVouchers', value: original.totalVouchers },
            { key: 'userVouchers', value: original.userVouchers },
            { key: 'mirrorVouchers', value: original.mirrorVouchers },
            { key: 'whitelistVouchers', value: original.whitelistVouchers },
          ].map(({ key, value }) => (
            <Paper key={key} variant="outlined" sx={{ px: 2, py: 1.5, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" display="block">
                {tGrand(t, `field.${key}`)}
              </Typography>
              <Typography variant="h5" fontWeight={700}>
                {value ?? '-'}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Card>

      <Card sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h6">{tGrand(t, 'section.rule')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {slotCopy.ruleDesc}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {tGrand(t, 'section.ruleNote')}
            </Typography>
          </Box>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
            mt: 2.5,
          }}
        >
          <Field label={tGrand(t, 'field.openHoursBeforeKickoff')}>
            <NumberInput
              value={config.openHoursBeforeKickoff}
              suffix={tGrand(t, 'unit.hour')}
              onChange={(value) => onChange({ ...config, openHoursBeforeKickoff: value })}
              required
              disabled={locked || cardState !== 'awaiting'}
              min={1}
            />
          </Field>

          <Field label={tGrand(t, 'field.cutoffMinutesBeforeKickoff')}>
            <NumberInput
              value={config.cutoffMinutesBeforeKickoff}
              suffix={tGrand(t, 'unit.minute')}
              onChange={(value) => onChange({ ...config, cutoffMinutesBeforeKickoff: value })}
              required
              disabled={locked || cardState !== 'awaiting'}
              min={0}
            />
          </Field>

          <Field label={tGrand(t, 'field.perUserStakeCap')}>
            <NumberInput
              value={config.perUserStakeCap}
              suffix={tGrand(t, 'unit.ticketUnlimited')}
              onChange={(value) => onChange({ ...config, perUserStakeCap: value })}
              disabled={locked || cardState !== 'awaiting'}
              required
              min={0}
            />
          </Field>

          <Field label={tGrand(t, 'field.perMatchStakeCap')}>
            <NumberInput
              value={config.perMatchStakeCap}
              suffix={tGrand(t, 'unit.ticketUnlimited')}
              onChange={(value) => onChange({ ...config, perMatchStakeCap: value })}
              disabled={locked || cardState !== 'awaiting'}
              required
              min={0}
            />
          </Field>

          <Field label={tGrand(t, 'field.poolUsdc')}>
            <MoneyInput
              value={config.poolUsdc}
              disabled={locked || cardState !== 'awaiting'}
              onChange={(value) => onChange({ ...config, poolUsdc: value })}
            />
          </Field>

          <Field label={tGrand(t, 'field.recoveryRatio')}>
            <RecoveryRatioInput
              value={Number(config.recoveryRatio ?? 0)}
              disabled={locked || cardState !== 'awaiting'}
              onChange={(value) => onChange({ ...config, recoveryRatio: value })}
            />
          </Field>
        </Box>

        <Paper
          variant="outlined"
          sx={{
            mt: 2,
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {tGrand(t, 'field.visibleOnFront')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {slotCopy.visibleDesc}
            </Typography>
          </Box>

          <Switch
            checked={config.visibleOnFront}
            onChange={(_, checked) => onChange({ ...config, visibleOnFront: checked })}
          />
        </Paper>
      </Card>
    </>
  );
});

GrandPrixForm.displayName = 'GrandPrixForm';

function getSlotCopy(t: TranslateFn, slot: GrandPrixSlot) {
  return {
    basicDesc: tGrand(t, `slotCopy.${slot}.basicDesc`),
    ruleDesc: tGrand(t, `slotCopy.${slot}.ruleDesc`),
    visibleDesc: tGrand(t, `slotCopy.${slot}.visibleDesc`),
  };
}

const Field = memo(({ label, children }: FieldProps) => (
  <Box>
    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
      {label}
    </Typography>
    {children}
  </Box>
));

Field.displayName = 'Field';

const TextInput = memo(
  ({ value, onChange }: { value: string; onChange: (value: string) => void }) => (
    <TextField
      size="small"
      fullWidth
      value={value}
      inputProps={{ maxLength: 64 }}
      onChange={(event) => onChange(event.target.value)}
    />
  )
);

TextInput.displayName = 'TextInput';

const MoneyInput = memo(
  ({
    value,
    disabled,
    onChange,
  }: {
    value: string;
    disabled?: boolean;
    onChange: (value: string) => void;
  }) => (
    <TextField
      size="small"
      fullWidth
      value={value}
      disabled={disabled}
      InputProps={{
        startAdornment: <InputAdornment position="start">$</InputAdornment>,
      }}
      inputProps={{ inputMode: 'decimal' }}
      onChange={(event) => {
        const filtered = event.target.value.replace(/[^\d.]/g, '');
        onChange(filtered);
      }}
    />
  )
);

MoneyInput.displayName = 'MoneyInput';

const NumberInput = memo(
  ({
    value,
    suffix,
    onChange,
    required = false,
    min = 1,
    disabled,
  }: {
    min?: number;
    value: number;
    suffix?: string;
    onChange: (value: number) => void;
    required?: boolean;
    disabled?: boolean;
  }) => {
    const t = useTranslate();
    const isInvalid = required && (!Number.isFinite(value) || value < min);
    const displayValue = Number.isFinite(value) ? value : '';

    return (
      <TextField
        size="small"
        fullWidth
        value={displayValue}
        required={required}
        error={isInvalid}
        helperText={isInvalid ? t('ra.validation.required') : undefined}
        disabled={disabled}
        onChange={(event) => {
          const cleaned = event.target.value.replace(/[^\d]/g, '');
          if (cleaned === '') {
            onChange(undefined as unknown as number);
            return;
          }
          const next = Number(cleaned);
          if (Number.isFinite(next)) {
            onChange(next);
          }
        }}
        InputProps={{
          endAdornment: suffix ? (
            <InputAdornment position="end">{suffix}</InputAdornment>
          ) : undefined,
        }}
        inputProps={{ inputMode: 'numeric' }}
      />
    );
  }
);

NumberInput.displayName = 'NumberInput';

const RecoveryRatioInput = memo(
  ({
    value,
    onChange,
    disabled,
  }: {
    value: number;
    onChange: (value: number) => void;
    disabled?: boolean;
  }) => {
    const [text, setText] = useState<string>(() => formatRatio(value));

    useEffect(() => {
      const externalNum = Number.isFinite(value) ? value : 0;
      const currentNum = Number(text.replace(',', '.'));
      if (!Number.isFinite(currentNum) || currentNum !== externalNum) {
        setText(formatRatio(externalNum));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    return (
      <TextField
        size="small"
        fullWidth
        value={text}
        disabled={disabled}
        onChange={(e) => {
          const raw = e.target.value.replace(/\D/g, '');
          if (raw === '') {
            setText('');
            onChange(0);
            return;
          }
          const num = Number(raw);
          if (!Number.isFinite(num) || num < 0 || num > 99) return;
          setText(String(num));
          onChange(num);
        }}
        inputProps={{ inputMode: 'numeric' }}
        InputProps={{
          endAdornment: <InputAdornment position="end">%</InputAdornment>,
        }}
      />
    );
  }
);

RecoveryRatioInput.displayName = 'RecoveryRatioInput';

function formatRatio(num: number): string {
  if (!Number.isFinite(num)) return '0';
  return String(Math.trunc(num));
}

const SaveBar = memo(({ loading, onDiscard, onSave }: SaveBarProps) => {
  const t = useTranslate();

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'sticky',
        bottom: 16,
        mt: 3,
        px: 3,
        py: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        zIndex: (theme) => theme.zIndex.appBar,
      }}
    >
      <Box>
        <Typography variant="subtitle1" fontWeight={700}>
          {tGrand(t, 'saveBar.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {tGrand(t, 'saveBar.desc')}
        </Typography>
      </Box>

      <Stack direction="row" spacing={1.5}>
        <Button variant="outlined" disabled={loading} onClick={onDiscard}>
          {tGrand(t, 'actions.discard')}
        </Button>

        <PermissionButton resource="world-cup-grand-prix" action="write">
          <Button variant="contained" disabled={loading} onClick={onSave}>
            {loading ? t('common.saving') : tGrand(t, 'actions.save')}
          </Button>
        </PermissionButton>
      </Stack>
    </Paper>
  );
});

SaveBar.displayName = 'SaveBar';
